import { FormEvent, useState } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { formatMoney } from "@/lib/formatters"
import { cn } from "@/lib/utils"
import {
  TILL_PATHS,
  cashDifference,
  differenceTone,
  tTill,
  type ShiftReport,
} from "@/features/staff/till"

/**
 * The close is the one ceremony on this screen.
 *
 * Everything else here commits quietly, because everything else is undoable: a
 * wrong cash movement is corrected by another movement. The close is not. The
 * shift refuses to reopen, the cash and terminal rails lock until someone opens
 * a new one, and the number produced is the one the restaurant will argue about
 * later. So it asks — and it asks with the two figures that make the answer
 * meaningful side by side: what the drawer should hold, and what was counted.
 *
 * The difference appears *before* confirming, not after. A cashier who
 * discovers a four-hundred-peso shortfall on the read-only Z has no move left;
 * one who sees it while the dialog is still open can recount.
 *
 * Open bills are warned about rather than used to disable the button. The
 * server refuses the close on its own (`ShiftClosingService.blocking_bill_ids`),
 * so the warning is there to save a wasted drawer count, not to be the guard —
 * and a disabled control with no explanation is how a cashier ends up
 * phoning their manager instead of settling the bill.
 */

type TillCloseDialogProps = {
  report: ShiftReport
}

function DifferencePreview({ expectedCash, counted }: { expectedCash: string; counted: string }) {
  const difference = cashDifference(expectedCash, counted)

  if (difference === null) {
    return (
      <div
        className="flex items-baseline justify-between gap-4 rounded-md border px-3 py-2"
        data-testid="shift-difference"
      >
        <span className="text-sm text-muted-foreground">{tTill("difference_label")}</span>
        <span className="text-right text-xs text-muted-foreground">
          {tTill("difference_pending")}
        </span>
      </div>
    )
  }

  const tone = differenceTone(difference)

  return (
    <div
      className="flex items-baseline justify-between gap-4 rounded-md border px-3 py-2"
      data-testid="shift-difference"
      data-tone={tone}
    >
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        {/* The dot rule: state is a small mark, and the word beside it carries
            the same meaning without colour vision. */}
        <span
          aria-hidden="true"
          className={cn(
            "size-2 shrink-0 rounded-full",
            tone === "balanced" && "bg-success",
            tone === "over" && "bg-warning",
            tone === "short" && "bg-destructive"
          )}
        />
        {tTill("difference_label")}
      </span>
      <span className="text-right">
        <span className="block text-sm font-semibold tabular-nums text-foreground">
          {formatMoney(difference)}
        </span>
        <span className="block text-xs text-muted-foreground">{tTill(`difference_${tone}`)}</span>
      </span>
    </div>
  )
}

export function TillCloseDialog({ report }: TillCloseDialogProps) {
  const [open, setOpen] = useState(false)
  const { data, setData, patch, processing, reset } = useForm({ declared_cash: "" })
  const blockingBills = report.blockingBillCount ?? 0

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) reset()
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    // `preserveState` keeps this dialog mounted through the redirect. A refused
    // close (open bills, a shift someone else already signed) comes back as a
    // flash alert, and the cashier needs to still be looking at the field they
    // just filled when it lands. On a successful close the shift stops being
    // open, the summary that owns this dialog unmounts, and it goes with it.
    patch(TILL_PATHS.close(report.shift.id), { preserveScroll: true, preserveState: true })
  }

  return (
    <>
      <Button type="button" onClick={() => handleOpenChange(true)} data-testid="shift-close-open">
        {tTill("close_open")}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tTill("close_title")}</DialogTitle>
            <DialogDescription>{tTill("close_description")}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {blockingBills > 0 ? (
              <p
                className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
                data-testid="shift-blocking-bills"
              >
                {tTill("blocking_bills", { count: blockingBills })}
              </p>
            ) : null}

            <div className="flex items-baseline justify-between gap-4 rounded-md border px-3 py-2">
              <span className="text-sm text-muted-foreground">{tTill("expected_cash")}</span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {formatMoney(report.expectedCash)}
              </span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="declared-cash">{tTill("declared_label")}</Label>
              <Input
                id="declared-cash"
                data-testid="shift-declared-cash"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                required
                autoFocus
                autoComplete="off"
                className="tabular-nums"
                aria-describedby="declared-cash-hint"
                value={data.declared_cash}
                onChange={(event) => setData("declared_cash", event.target.value)}
              />
              <p id="declared-cash-hint" className="text-xs text-muted-foreground">
                {tTill("declared_hint")}
              </p>
            </div>

            <DifferencePreview expectedCash={report.expectedCash} counted={data.declared_cash} />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                data-testid="shift-close-cancel"
              >
                {tTill("close_cancel")}
              </Button>
              <Button type="submit" disabled={processing} data-testid="shift-close-confirm">
                {tTill("close_confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
